import { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/lt";
import dayLocaleData from "dayjs/plugin/localeData";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import debounce from "lodash.debounce";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { notification } from "antd";
import type { FormConfig } from "../../App";
import { getAttribution } from "./attribution";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(dayLocaleData);
dayjs.locale("lt");

export interface City {
  uuid: string;
  name: string;
}

export interface CampDateInfo {
  date: string;
  full_day_enabled: boolean;
  half_day_enabled: boolean;
}

export interface UseFormBaseOptions {
  reservationType: string | null;
  formConfig: FormConfig;
  onSubmitSuccess: () => void;
  formTemplate: string;
  campDates?: CampDateInfo[];
  selectedWeek?: string;
}

export function useFormBase({
  reservationType,
  formConfig,
  onSubmitSuccess,
  formTemplate,
  campDates,
  selectedWeek,
}: UseFormBaseOptions) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [services, setServices] = useState<any[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [price, setPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTime, setActiveTime] = useState<string | null>(null);
  const [availableTimes, setAvailableTimes] = useState<any[]>([]);
  const [isPosting, setPosting] = useState<boolean>(false);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [combo, setCombo] = useState(false);
  const [discount, setDiscount] = useState({ discount_amount: 0, uuid: "" });
  const [discountCode, setDiscountCode] = useState("");
  const [discountStatus, setDiscountStatus] = useState<boolean | null>(null);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Contact fields
  const [parentName, setParentName] = useState("");
  const [phone, setPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [address, setAddress] = useState("");
  const [guestCount, setGuestCount] = useState<number>(1);
  const [childName, setChildName] = useState("");
  const [childBirthDate, setChildBirthDate] = useState("");
  const [childAge, setChildAge] = useState<number>(0);

  // Organization fields
  const [organizationName, setOrganizationName] = useState("");
  const [classInfo, setClassInfo] = useState("");
  const [invoiceRequired, setInvoiceRequired] = useState(false);
  const [campPassType, setCampPassType] = useState("full_day");
  const [comments, setComments] = useState("");

  // Extension fields
  const [isExtensionEnabled, setIsExtensionEnabled] = useState(false);
  const [isFirstTimeSlotSelected, setIsFirstTimeSlotSelected] = useState(false);

  // Fetch cities on mount
  useEffect(() => {
    axios
      .get(`${apiBaseUrl}/api/cities`)
      .then((res) => {
        // Cities API returns plain array or { data: [...] }
        const citiesList = Array.isArray(res.data) ? res.data : res.data?.data;
        if (citiesList && citiesList.length > 0) {
          setCities(citiesList);
          // If formConfig has cities, filter; otherwise use all
          if (formConfig.cities.length > 0) {
            const configCityUuids = formConfig.cities.map((c) => c.uuid);
            const filtered = citiesList.filter((c: City) =>
              configCityUuids.includes(c.uuid)
            );
            if (filtered.length === 1) {
              setSelectedCity(filtered[0]);
            }
            setCities(filtered);
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  // Fetch services when city changes
  useEffect(() => {
    if (!selectedCity) return;
    axios
      .get(`${apiBaseUrl}/api/service/get/all`, {
        params: {
          city_uuid: selectedCity.uuid,
          reservation_type: reservationType,
        },
      })
      .then((res) => {
        const servicesList = Array.isArray(res.data) ? res.data : res.data?.data;
        if (servicesList) {
          setServices(servicesList);
        }
      })
      .catch(() => {});
  }, [selectedCity]);

  // Fetch available times
  const fetchTimes = useCallback(
    debounce((cityUuid: string, date: string) => {
      axios
        .get(`${apiBaseUrl}/api/calendar/get/freetimes`, {
          params: {
            city_uuid: cityUuid,
            date: date,
            reservation_type: reservationType,
          },
        })
        .then((res) => {
          const timesList = Array.isArray(res.data) ? res.data : res.data?.data;
          if (timesList) {
            setAvailableTimes(timesList);
          }
        })
        .catch(() => {
          setAvailableTimes([]);
        });
    }, 300),
    [reservationType]
  );

  // Calculate price
  const calculatePrice = useCallback(() => {
    if (!activeTime && formTemplate !== "camp") return;

    let total = 0;

    if (formTemplate === "camp") {
      const campTime = availableTimes.find(
        (t: any) => t.camp_pass_type === campPassType
      );
      const basePrice = campTime?.base_price || 0;

      let numDays = 5;
      if (selectedWeek && campDates && campDates.length > 0) {
        const monday = dayjs(selectedWeek);
        const weekDates: string[] = [];
        for (let i = 0; i < 5; i++) {
          weekDates.push(monday.add(i, "day").format("YYYY-MM-DD"));
        }
        numDays = campDates.filter(
          (cd) =>
            weekDates.includes(cd.date) &&
            (campPassType === "full_day"
              ? cd.full_day_enabled
              : cd.half_day_enabled)
        ).length;
      }

      // Apply discount to base price BEFORE prorating, so a 4-day week
      // gets 4/5 of the discount too (keeps per-day price consistent).
      const campDiscount = discount.discount_amount > 0 ? discount.discount_amount : 0;
      const effectiveBase = Math.max(0, basePrice - campDiscount);

      if (numDays >= 5 || numDays === 0) {
        total = numDays === 0 ? 0 : effectiveBase;
      } else {
        total = Math.ceil((effectiveBase / 5) * numDays);
      }
    } else if (formTemplate === "education") {
      total = guestCount * 7;
    } else if (formTemplate === "graduation") {
      total = guestCount >= 15 ? guestCount * 10 : 150;
    } else {
      // Birthday, private_party, seminar: use time slot base_price
      const timeSlot = availableTimes.find((t: any) => t.uuid === activeTime);
      if (timeSlot) {
        total = timeSlot.base_price || 0;
      }
    }

    // Add service prices
    for (const sUuid of selectedServices) {
      const svc = services.find((s: any) => s.uuid === sUuid);
      if (svc) {
        total += svc.price || 0;
      }
    }

    // Combo discount
    const comboUuids = [
      "fe2b259a25da426db77eca7b0aa50ba5",
      "2c16e8c047734c72999241d3c88d481a",
    ];
    const hasCombo = comboUuids.every((u) => selectedServices.includes(u));
    setCombo(hasCombo);
    if (hasCombo) total -= 10;

    // Discount code: for camp it's already folded into effectiveBase above
    // (so it gets prorated with numDays). For all other forms, subtract flat.
    if (formTemplate !== "camp" && discount.discount_amount > 0) {
      total -= discount.discount_amount;
    }

    setPrice(Math.max(0, total));
  }, [
    activeTime,
    selectedServices,
    services,
    availableTimes,
    discount,
    formTemplate,
    campPassType,
    guestCount,
    campDates,
    selectedWeek,
  ]);

  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  // Check discount
  const checkDiscount = useCallback(() => {
    if (!discountCode || !selectedCity || !selectedDate) return;

    axios
      .get(`${apiBaseUrl}/api/discount/check`, {
        params: {
          code: discountCode,
          city_uuid: selectedCity.uuid,
          day_selected: selectedDate,
        },
      })
      .then((res) => {
        if (res.data?.status === 200 && res.data?.discount) {
          setDiscount(res.data.discount);
          setDiscountStatus(true);
        } else {
          setDiscountStatus(false);
          setDiscount({ discount_amount: 0, uuid: "" });
        }
      })
      .catch(() => {
        setDiscountStatus(false);
        setDiscount({ discount_amount: 0, uuid: "" });
      });
  }, [discountCode, selectedCity, selectedDate]);

  // Submit order
  const submitOrder = useCallback(
    async (extraData: Record<string, any> = {}) => {
      if (isPosting) return;

      setPosting(true);

      try {
        let recaptchaToken = "";
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("submit_order");
        }

        const orderData: Record<string, any> = {
          form_template: formTemplate,
          city_uuid: selectedCity?.uuid,
          day_selected: selectedDate,
          customer_parent_name: parentName,
          customer_phone_no: phone,
          customer_email: customerEmail,
          customer_address: address,
          customer_guests: guestCount,
          services_selected: selectedServices,
          discount_code: discountCode,
          marketing_consent: marketingConsent,
          data_processing_consent: true,
          recaptcha_token: recaptchaToken,
          ...getAttribution(),
          ...extraData,
        };

        // Add time_selected for all forms
        if (formTemplate === "camp") {
          const campTime = availableTimes.find(
            (t: any) => t.camp_pass_type === campPassType
          );
          if (campTime) {
            orderData.time_selected = campTime.uuid;
          }
        } else if (activeTime) {
          orderData.time_selected = activeTime;
          orderData.is_first_time_slot = isFirstTimeSlotSelected;
        }

        // Add child info for applicable forms
        if (["birthday", "private_party", "camp"].includes(formTemplate)) {
          if (childName) orderData.customer_name = childName;
          if (childAge) orderData.customer_age = childAge;
          if (childBirthDate) orderData.child_birth_date = childBirthDate;
        }

        // Add org info for applicable forms
        if (["education", "seminar", "graduation"].includes(formTemplate)) {
          orderData.organization_name = organizationName;
          if (classInfo) orderData.class_info = classInfo;
        }

        if (formTemplate === "seminar") {
          orderData.invoice_required = invoiceRequired;
        }

        if (formTemplate === "camp") {
          orderData.camp_pass_type = campPassType;
          orderData.reservation_type_uuid = reservationType;
          if (comments) orderData.comments = comments;
        }

        const res = await axios.post(
          `${apiBaseUrl}/api/order/add`,
          orderData
        );

        if (res.data?.payload?.status === "OK") {
          onSubmitSuccess();
        } else {
          notification.error({
            message: "Klaida",
            description:
              "Nepavyko pateikti užsakymo. Bandykite dar kartą.",
          });
        }
      } catch (err: any) {
        console.error("Order submission error:", err);
        const errMsg =
          err.response?.data?.payload?.errors ||
          "Nepavyko pateikti užsakymo.";
        notification.error({
          message: "Klaida",
          description:
            typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
        });
      } finally {
        setPosting(false);
      }
    },
    [
      isPosting,
      formTemplate,
      selectedCity,
      selectedDate,
      parentName,
      phone,
      customerEmail,
      address,
      guestCount,
      selectedServices,
      discountCode,
      marketingConsent,
      dataProcessingConsent,
      activeTime,
      isFirstTimeSlotSelected,
      childName,
      childAge,
      childBirthDate,
      organizationName,
      classInfo,
      invoiceRequired,
      campPassType,
      availableTimes,
      reservationType,
      executeRecaptcha,
      onSubmitSuccess,
    ]
  );

  const nextStep = () => setCurrentStep((s) => s + 1);
  const prevStep = () => setCurrentStep((s) => Math.max(0, s - 1));

  const handleCityChange = (city: City) => {
    setSelectedCity(city);
    setActiveTime(null);
    setAvailableTimes([]);
    setSelectedDate("");
    setSelectedServices([]);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setActiveTime(null);
    if (selectedCity && date) {
      fetchTimes(selectedCity.uuid, date);
    }
  };

  const toggleService = (uuid: string) => {
    setSelectedServices((prev) =>
      prev.includes(uuid)
        ? prev.filter((s) => s !== uuid)
        : [...prev, uuid]
    );
  };

  return {
    // State
    currentStep,
    setCurrentStep,
    services,
    selectedServices,
    setSelectedServices,
    price,
    loading,
    activeTime,
    setActiveTime,
    availableTimes,
    isPosting,
    selectedCity,
    cities,
    selectedDate,
    combo,
    discount,
    discountCode,
    setDiscountCode,
    discountStatus,
    confirmationChecked,
    setConfirmationChecked,
    dataProcessingConsent,
    setDataProcessingConsent,
    marketingConsent,
    setMarketingConsent,
    isExtensionEnabled,
    setIsExtensionEnabled,
    isFirstTimeSlotSelected,
    setIsFirstTimeSlotSelected,

    // Contact fields
    parentName,
    setParentName,
    phone,
    setPhone,
    customerEmail,
    setCustomerEmail,
    address,
    setAddress,
    guestCount,
    setGuestCount,
    childName,
    setChildName,
    childBirthDate,
    setChildBirthDate,
    childAge,
    setChildAge,

    // Organization fields
    organizationName,
    setOrganizationName,
    classInfo,
    setClassInfo,
    invoiceRequired,
    setInvoiceRequired,
    campPassType,
    setCampPassType,
    comments,
    setComments,

    // Actions
    nextStep,
    prevStep,
    handleCityChange,
    handleDateChange,
    toggleService,
    checkDiscount,
    submitOrder,
    fetchTimes,

    // Config
    apiBaseUrl,
    formConfig,
  };
}
